import { objectSpread2 as _objectSpread2, extends as _extends } from '../_virtual/_rollupPluginBabelHelpers.js';
import styled from '@emotion/styled';
import { createShouldForwardProp } from '@styled-system/should-forward-prop';
import memoizerific from 'memoizerific';
import React from 'react';
import { systemProps, resolveLayout, isSVGElement } from './system-props.esm.js';
import { css } from '../theme-provider/css.esm.js';
import styleVariant from '../theme-provider/variant.esm.js';
import { omit, objectFilter, memoizedGet, EMPTY_OBJECT } from '../utils/object.esm.js';
import { space } from './space.esm.js';
import { emSpace } from './em-space.esm.js';
import { color } from './color.esm.js';
import { typography } from './typography.esm.js';
import { position } from './position.esm.js';
import { border } from './border.esm.js';
import { shadow } from './shadow.esm.js';
import { flexbox } from './flexbox.esm.js';
import { grid } from './grid.esm.js';
import { stylable } from './stylable.esm.js';

var listElements = ['nav', 'ul', 'li', 'ol', 'dl', 'dt', 'dd'];
function getBaseReset(tag) {
  return _objectSpread2({
    margin: 'unset',
    padding: 'unset',
    border: 'unset',
    background: 'unset',
    font: 'unset',
    fontFamily: 'inherit',
    fontSize: '100%',
    boxSizing: 'border-box',
    outline: 'none'
  }, listElements.includes("".concat(tag)) ? {
    listStyle: 'none'
  } : {});
}
function resetCss(tag) {
  var baseResetCss = getBaseReset(tag);
  return function (props) {
    var _props$resetCss = props.resetCss,
      resetCss = _props$resetCss === void 0 ? EMPTY_OBJECT : _props$resetCss;
    var _resetCss = _objectSpread2(_objectSpread2({}, objectFilter(resetCss, function (v) {
      return v !== null && v !== undefined && "".concat(v).trim() !== '';
    })), omit(baseResetCss, Object.keys(resetCss)));
    return css(_resetCss)(props);
  };
}
function sx(props) {
  return css(props.sx)(props);
}
function base(props) {
  return css(props.__css)(props);
}
function variant(props) {
  var theme = props.theme,
    variant = props.variant,
    __themeKey = props.__themeKey;
  var styles = memoizedGet(theme, __themeKey ? __themeKey + '.' + variant : variant, memoizedGet(theme, variant));
  return css(styles);
}
var shouldForwardPropNonSvg = createShouldForwardProp(systemProps(false));
var shouldForwardPropSvg = createShouldForwardProp(systemProps(true));
var tui = memoizerific(1000)(function (tag) {
  var variants = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EMPTY_OBJECT;
  return styled(tag, {
    shouldForwardProp: function shouldForwardProp(propName) {
      var shouldForward = isSVGElement(tag) ? shouldForwardPropSvg : shouldForwardPropNonSvg;
      return shouldForward(propName) || propName === 'automation-id' || propName === 'replace';
    }
  })(resetCss(tag), styleVariant({
    variants: variants
  }), base, variant,
  // we include these styled-system css func evaluators to allow css props being past
  // to an element to be included in the resulting css class.
  space, emSpace, color, typography, resolveLayout(tag), position, border, shadow, flexbox, grid, stylable, sx, function (props) {
    return props.css;
  });
});
// DO NOT switch to standard EMPTY_OBJECT
var emptyObject = {};

/**
 * Creates a styled version of a component.
 *
 * @param as The component to style
 * @param displayName An optional display name override
 * @param variants Style variants available to the new component
 * @returns a {@link StyledComponent}
 *
 * @see [@emotion/styled](https://emotion.sh/docs/styled)
 */
function createComponent() {
  var as = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'div';
  var displayName = arguments.length > 1 ? arguments[1] : undefined;
  var variants = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : emptyObject;
  var Component = tui(as, variants);
  var render = /*#__PURE__*/React.forwardRef(function (props, ref) {
    return /*#__PURE__*/React.createElement(Component, _extends({
      ref: ref
    }, props));
  });
  render.displayName = displayName ? displayName : 'div';
  return render;
}

/**
 * Creates multiple styled components with the same display name.
 *
 * @param components The components to style
 * @param displayName The component's display name
 * @returns A map from original component names to their styled counterparts
 *
 * @see {@link createComponent}
 */
function createComponents(components, displayName, variants) {
  return components.reduce(function (acc, curr) {
    acc[curr] = createComponent(curr, displayName, variants);
    return acc;
  }, {});
}

export default createComponent;
export { createComponents, getBaseReset };
//# sourceMappingURL=create-component.esm.js.map
